import { Heading } from '@mm/components';
import { useEmailAccountCRUDActions } from '@mm/state/actions/EmailAccountCRUDActions';
import { selectEmailAccounts } from '@mm/state/selectors/emailAccountsSelectors';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import {
  hasDeactivatedNumberSelector,
  recentDiscardedSelector,
} from '@mm/state/selectors/twillio';
import { hasConnectedRealFacebookPageSelector } from '@mm/state/selectors/userSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import {
  INTEGRATIONS_NATIVE_INTEGRATIONS,
  SETTINGS_SMS_VIEW_NUMBER_PATH,
} from '../../../../../../common/constants/routes';
import { usePhoneNumber } from '../../../../../../common/hooks/use-phone-number';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import {
  ChannelIcon,
  ChannelType,
} from '../../../../liveChat/components/ChannelIcon';
import { useChannelConnection } from '../../Automations/AutomationEdit/InputTriggers/use-channel-connection';
import { useCreateChatStarter } from '../../ChatStarters/use-create-chat-starter';
import css from './ChannelsBlock.module.css';
import connectEmailIcon from './Icons/connect-email-icon.svg';
import connectFacebookIcon from './Icons/connect-fb-icon.svg';
import connectInstagramIcon from './Icons/connect-ig-icon.svg';
import connectSMSIcon from './Icons/connect-sms-icon.svg';
import connectWebchatIcon from './Icons/connect-webchat-icon.svg';
import openLinkIcon from './Icons/open-link-icon.svg';
import settingsIcon from './Icons/settings-icon.svg';

type ChannelProps = {
  icon: ChannelType;
  text: string;
  action?: () => void;
  connectButtonIcon?: string;
  actionIcon?: string;
};

const Channel = ({
  icon,
  text,
  action,
  connectButtonIcon,
  actionIcon,
}: ChannelProps) => {
  return (
    <div
      className={css.channelContainer}
      onClick={() => {
        action?.();
      }}
    >
      {!connectButtonIcon && (
        <>
          <ChannelIcon channel={icon} className={css.channelIcon} />
          <div className={css.channelText}> {text}</div>
          {actionIcon && <img src={actionIcon} className={css.actionIcon} />}
        </>
      )}
      {connectButtonIcon && (
        <>
          <img src={connectButtonIcon} className={css.channelIcon} />
          <PrimaryButton className={css.connectButton} color="secondary-blue">
            {text}
          </PrimaryButton>
        </>
      )}
    </div>
  );
};
export const ChannelsBlock = () => {
  const facebookPage = useSelector(activeFacebookPageSelector);
  const isUserPageConnected = useSelector(hasConnectedRealFacebookPageSelector);
  const chatbotId = useSelector(activeChatbotIdSelector);
  const firstChatbotId = useSelector(
    (state: StoreState) => state.chatbot.basicListByCompany[0]?.id,
  );
  const history = useHistory();
  const isZapierActivated = !!facebookPage?.zapierActivated;

  const accounts = useSelector(selectEmailAccounts);

  const { isSmsSupported, phoneNumber } = usePhoneNumber();

  const { getAccounts } = useEmailAccountCRUDActions();

  const {
    connectChannel,
    isFacebookConnected,
    isInstagramConnected,
    isEmailConnected,
  } = useChannelConnection(() => {}, chatbotId || firstChatbotId, false);

  const instagramUsername =
    facebookPage?.availableInstagramAccountsData?.username;

  const { createChatStarter } = useCreateChatStarter();

  const hasDeactivatedNumber = useSelector(hasDeactivatedNumberSelector);
  const recentDiscardedNumber = useSelector(recentDiscardedSelector);

  useEffect(() => {
    if (facebookPage?.id) {
      getAccounts(facebookPage.id);
    }
  }, [facebookPage?.id, getAccounts]);

  const channels: ChannelProps[] = [
    {
      icon: 'email',
      text: isEmailConnected ? accounts[0]?.emailAddress : 'Connect Email',
      connectButtonIcon: !isEmailConnected ? connectEmailIcon : undefined,
      action: () => {
        if (!accounts.length) {
          connectChannel('email');
          return;
        }

        history.push(INTEGRATIONS_NATIVE_INTEGRATIONS(chatbotId));
      },
      actionIcon: settingsIcon,
    },
    {
      icon: 'instagram',
      text: isInstagramConnected
        ? `@${instagramUsername}`
        : 'Connect Instagram',
      connectButtonIcon: !isInstagramConnected
        ? connectInstagramIcon
        : undefined,
      action: () => {
        if (!isInstagramConnected) {
          connectChannel('instagram');
          return;
        }

        window.open(`https://www.instagram.com/${instagramUsername}`, '_blank');
      },
      actionIcon: openLinkIcon,
    },
    {
      icon: 'facebook',
      text: isFacebookConnected ? facebookPage?.name || '' : 'Connect Facebook',
      connectButtonIcon: !isFacebookConnected ? connectFacebookIcon : undefined,
      action: () => {
        if (isUserPageConnected || facebookPage?.realFacebookPage) {
          window.open(
            `https://www.facebook.com/${facebookPage?.remoteId}`,
            '_blank',
          );
        } else {
          connectChannel('facebook');
        }
      },
      actionIcon: openLinkIcon,
    },
    {
      icon: 'webchat',
      text: facebookPage?.webChatActivated
        ? 'Customize WebChat'
        : 'Connect WebChat',
      connectButtonIcon: !facebookPage?.webChatActivated
        ? connectWebchatIcon
        : undefined,
      action: () => {
        if (facebookPage?.webChatActivated) {
          history.push(
            BotBuilderRoutes.chatStarters.getLink({
              chatbotId,
              kind: 'customer_chat_widget',
            }),
          );
          return;
        }

        createChatStarter('customer_chat_widget');
      },
      actionIcon: settingsIcon,
    },
  ];

  const smsChannel: ChannelProps = {
    icon: 'sms',
    text: phoneNumber
      ? phoneNumber.number
      : hasDeactivatedNumber
      ? `Restore ${recentDiscardedNumber} Phone Number`
      : 'Connect SMS Phone Number',
    connectButtonIcon:
      !phoneNumber || hasDeactivatedNumber ? connectSMSIcon : undefined,
    action: () => {
      if (!phoneNumber) {
        connectChannel('sms');
        return;
      }
      history.push(SETTINGS_SMS_VIEW_NUMBER_PATH(chatbotId));
    },
    actionIcon: settingsIcon,
  };

  if (isSmsSupported) {
    channels.splice(1, 0, smsChannel);
  }

  return (
    <div className={css.container}>
      <Heading size={2} className={css.heading}>
        My channels
      </Heading>
      {channels.map((c, idx) => (
        <Channel
          icon={c.icon}
          text={c.text}
          action={c.action}
          key={idx}
          actionIcon={c.actionIcon}
          connectButtonIcon={c.connectButtonIcon}
        />
      ))}
    </div>
  );
};
